// Copyright (c) StrangeBee 2023

import { useLocalMessage } from '@Helpers/locale';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import useConfigList from '@Hooks/useConfigList';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import React, { ReactElement } from 'react';
import { TagOutput, ObjectTypeV1 as ObjectType, AdminPermissionsEnum, PermissionsEnum } from 'thehive-sdk';
import { Space } from 'antd';
import EditableColor from '@Molecules/EditableColor/EditableColor';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import EditableText from '@Molecules/EditableText/EditableText';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import client, { queryClient } from '@Client';
import DisplayTag from '@Organisms/DisplayTag/DisplayTag';
import { THLayout } from '@Molecules/THLayout';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { useListAllFields } from '@Queries/describeQueries';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { ICONS } from '@Constants/iconsApp';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { FormatMultipleDate } from '@Molecules/Format';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import useUserPermissions from '@Hooks/useUserPermissions';
import { computeListSelector } from '@Organisms/CustomTag/selector';
import usePageTitle from '@Hooks/usePageTitle';
import { useParams } from 'react-router-dom';

import './CustomTagsList.scss';

const CustomTagsList = (): ReactElement => {
    const i = useLocalMessage();
    const { orgId } = useParams<{ orgId: string }>();

    const config = useConfigList<TagOutput>({
        selector: computeListSelector(),
        controller: client.CustomTagController,
        storageId: StorageFilterEnum.CUSTOM_TAGS,
        objectType: ObjectType.TAG,
    });

    const {
        countRefetch,
        dataSource = [],
        columnsRenderer,
        actionListRenderer,
        status = 'loading',
        table,
        filterManager,
        paginationManager,
        storagePageManager,
        storageViewManager,
    } = config;

    const hasPermission = useUserPermissions({ permissionsNeeded: [AdminPermissionsEnum.MANAGE_TAG] });

    const actionList = actionListRenderer((selectedItems, onSuccess) => {
        const firstCustomTag = selectedItems.find(Boolean);

        return {
            delete: {
                query: (): Promise<void> =>
                    client.CustomTagController.delete(firstCustomTag?._id || '')
                        .then(() => {
                            openMessage('success', i('customTags.list.actions.delete.success'));
                            onSuccess(true);
                        })
                        .catch(handleNotificationError),
                label: i('customTags.list.actions.delete.label'),
                icon: <ICONS.DeleteIcon />,
                confirm: true,
                message: i('customTags.list.actions.delete.single'),
                scope: 'single',
            },
        };
    });

    const columns = columnsRenderer([
        {
            title: i('customTags.list.columns.name'),
            className: 'custom-tags-list__name',
            render: (tag): ReactElement => (
                <EditableText
                    customComponent={<DisplayTag tag={tag} />}
                    saveValue={(value): Promise<void> =>
                        client.CustomTagController.edit({ predicate: value }, tag._id)
                            .then(() => {
                                queryClient.refetchQueries([computeListSelector().name]);
                            })
                            .catch(handleNotificationError)
                    }
                    text={tag.predicate}
                />
            ),
        },
        {
            title: i('customTags.list.columns.colour'),
            className: 'custom-tags-list__colour',
            render: ({ colour, _id }): ReactElement => (
                <Space direction="horizontal">
                    <EditableColor
                        defaultColor={colour}
                        onConfirm={(color): Promise<void> =>
                            client.CustomTagController.edit({ colour: color }, _id).then(() => {
                                queryClient.refetchQueries([computeListSelector().name]);
                            })
                        }
                    />
                </Space>
            ),
        },
        {
            title: i('customTags.list.columns.cases'),
            className: 'custom-tags-list__cases',
            render: ({ extraData }): ReactElement => <div>{extraData?.usage?.case}</div>,
        },
        {
            title: i('customTags.list.columns.alerts'),
            className: 'custom-tags-list__alerts',
            render: ({ extraData }): ReactElement => <div>{extraData?.usage?.alert}</div>,
        },
        {
            title: i('customTags.list.columns.observables'),
            className: 'custom-tags-list__observables',
            render: ({ extraData }): ReactElement => <div>{extraData?.usage?.observable}</div>,
        },
        {
            title: i('customTags.list.columns.templates'),
            className: 'custom-tags-list__templates',
            render: ({ extraData }): ReactElement => <div>{extraData?.usage?.caseTemplate}</div>,
        },
        {
            title: i('customTags.list.columns.by'),
            className: 'custom-tags-list__by',
            render: ({ _createdBy }): ReactElement => <AssigneeInfo assignee={_createdBy} />,
        },
        {
            title: [
                { label: i('customTags.list.columns.dates') },
                { label: 'C.', sortField: '_createdAt' },
                { label: 'U.', sortField: '_updatedAt' },
            ],
            className: 'custom-tags-list__dates',
            render: ({ _createdAt = 0, _updatedAt = 0 }, { date: filterDate }): ReactElement => (
                <FormatMultipleDate
                    dates={[
                        {
                            label: 'C.',
                            value: _createdAt,
                            onClick: (): void => filterDate(_createdAt, '_createdAt'),
                        },
                        {
                            label: 'U.',
                            value: _updatedAt,
                            onClick: (): void => filterDate(_updatedAt, '_updatedAt'),
                        },
                    ]}
                />
            ),
        },
        {
            className: 'custom-tags-list__actions',
            render: (customTag): ReactElement | null =>
                hasPermission ? (
                    <ActionsList
                        actionList={actionList([customTag], (count) => {
                            queryClient.refetchQueries([computeListSelector().name]);
                            if (count) {
                                countRefetch();
                            }
                        })}
                        scope="single"
                        isIcon
                    />
                ) : null,
        },
    ]);

    const { data: describe } = useListAllFields();

    usePageTitle(i('page-title.organisation.custom-tags', { companyName: orgId }));

    return (
        <THLayout>
            <THList>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        {storagePageManager && storageViewManager ? (
                            <ManageViewList
                                pageManager={storagePageManager}
                                viewManager={storageViewManager}
                                beforeCreate={(content: any): any => {
                                    filterManager.applyFilters();
                                    return { ...content, filters: filterManager.tempFilters };
                                }}
                            />
                        ) : null}
                    </THList.Slot>
                    <THList.Slot name="NAVIGATION">
                        <ManageSwitch
                            label={i('common.filters')}
                            value={filterManager.editFilters}
                            onChange={(editFilters): void => {
                                filterManager.setEditFilters(editFilters);
                            }}
                            icon={<ICONS.FilterOutlined />}
                        />
                    </THList.Slot>

                    <THList.Slot name="CONTENT">
                        {(filterManager.editFilters || !!filterManager.tempFilters.length) && (
                            <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.TAG]}>
                                <Footer filterConfig={filterManager} />
                            </THFilter>
                        )}
                    </THList.Slot>
                </THList.Header>

                <THList.Content
                    dataSource={dataSource}
                    columns={columns}
                    rowKey="_id"
                    emptyPlaceholder={<DisplayEmptyData spin={status === 'loading'} label={i('customTags.none')} />}
                />

                <THList.Footer>
                    <THList.Slot name="NAVIGATION">
                        <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                        <PaginationSize paginationConfig={paginationManager} />
                    </THList.Slot>
                </THList.Footer>
            </THList>
        </THLayout>
    );
};

const path = RouteBuilder.compute('administration.customTags');
const pathUser = RouteBuilder.compute('organisation.customTags');

export const Route: RouteType = {
    menu: {
        label: 'organisation.layout.nav-bar.custom-tags',
        icon: <ICONS.TagOutlined />,
    },
    path,
    content: <CustomTagsList />,
    permissionNeeded: [PermissionsEnum.MANAGE_TAG],
    shared: true,
    space: ['admin'],
    breadcrumb: { label: 'breadcrumb.organisation.custom-tags' },
};

export const RouteUser: RouteType = {
    ...Route,
    path: pathUser,
    space: ['user'],
};
